import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react'
import { fmtInt, longDay, niceMax, pct, shortDay } from '@/lib/statsFormat'
import styles from './charts.module.css'

export interface ChartSeries {
  key: string
  label: string
  values: number[]
  /** CSS-цвет ряда: токен страницы, например var(--chart-1). */
  color: string
}

const H = 176
const TOP = 12
const BOTTOM = 24
const RIGHT = 14

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null)
  const [w, setW] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    setW(el.clientWidth)
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e?.contentRect.width ?? 0)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, w]
}

/*
 * Ряд по дням: одна линия с заливкой или несколько линий с легендой. Ось Y одна и от нуля;
 * сетка — волосяные линии; подпись значения — только на конце линии. Наведение (и стрелки
 * с клавиатуры) ставит перекрестие на ближайший день и показывает все ряды в одной подсказке.
 */
export function TimeChart({
  title,
  sub,
  days,
  series,
  format = fmtInt,
}: {
  title: string
  sub?: ReactNode
  days: string[]
  series: ChartSeries[]
  format?: (n: number) => string
}) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)
  const n = days.length
  const single = series.length === 1
  const max = niceMax(Math.max(0, ...series.flatMap((s) => s.values)))
  const ticks = [0, max / 2, max]
  const left = Math.max(26, ...ticks.map((t) => format(t).length * 7 + 10))
  const iw = Math.max(1, width - left - RIGHT)
  const ih = H - TOP - BOTTOM
  const x = (i: number) => left + (n <= 1 ? iw / 2 : (i * iw) / (n - 1))
  const y = (v: number) => TOP + ih - (v / max) * ih

  const xTicks = useMemo(() => {
    if (n === 0) return []
    const count = Math.max(2, Math.min(7, Math.floor(iw / 84)))
    const out = new Set<number>()
    for (let k = 0; k < count; k++) out.add(Math.round((k * (n - 1)) / (count - 1)))
    return [...out]
  }, [n, iw])

  const line = (values: number[]) => values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('')
  const pick = (clientX: number, el: Element) => {
    const r = el.getBoundingClientRect()
    const px = clientX - r.left
    const i = n <= 1 ? 0 : Math.round(((px - left) / iw) * (n - 1))
    setHover(Math.max(0, Math.min(n - 1, i)))
  }
  const onKey = (e: KeyboardEvent) => {
    if (n === 0) return
    const cur = hover ?? n - 1
    if (e.key === 'ArrowLeft') setHover(Math.max(0, cur - 1))
    else if (e.key === 'ArrowRight') setHover(Math.min(n - 1, cur + 1))
    else if (e.key === 'Home') setHover(0)
    else if (e.key === 'End') setHover(n - 1)
    else if (e.key === 'Escape') setHover(null)
    else return
    e.preventDefault()
  }

  // подписи на концах: только если не налезают друг на друга
  const ends = series.map((s) => ({ s, v: s.values[n - 1] ?? 0 }))
  const endsFit = ends.length < 2 || Math.abs(y(ends[0]!.v) - y(ends[1]!.v)) > 16
  const last = n > 0 ? days[n - 1]! : ''
  const summary = `${title}: ${series.map((s) => `${s.label} ${format(s.values[n - 1] ?? 0)} на ${last ? shortDay(last) : '—'}`).join(', ')}`

  return (
    <section className={styles.chart}>
      <div className={styles.head}>
        <h3 className={styles.title}>{title}</h3>
        {sub ? <span className={styles.sub}>{sub}</span> : null}
      </div>
      {!single ? (
        <div className={styles.legend}>
          {series.map((s) => (
            <span key={s.key} className={styles.legendItem}>
              <span className={styles.key} style={{ background: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      ) : null}
      <div
        ref={ref}
        className={styles.plot}
        tabIndex={0}
        role="group"
        aria-label={`${summary}. Стрелки влево и вправо — по дням`}
        onKeyDown={onKey}
        onFocus={() => setHover((h) => h ?? n - 1)}
        onBlur={() => setHover(null)}
      >
        {width > 0 ? (
          <svg className={styles.svg} width={width} height={H} role="img" aria-label={summary}>
            {ticks.map((t) => (
              <g key={t}>
                <line className={styles.grid} x1={left} x2={left + iw} y1={Math.round(y(t)) + 0.5} y2={Math.round(y(t)) + 0.5} />
                <text className={styles.tick} x={left - 8} y={y(t) + 4} textAnchor="end">{format(t)}</text>
              </g>
            ))}
            {xTicks.map((i) => (
              <text key={i} className={styles.tick} x={x(i)} y={H - 6} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}>
                {shortDay(days[i]!)}
              </text>
            ))}
            {series.map((s) => (
              <g key={s.key}>
                {single && n > 1 ? (
                  <path d={`${line(s.values)}L${x(n - 1).toFixed(1)},${y(0)}L${x(0).toFixed(1)},${y(0)}Z`} fill={s.color} opacity={0.1} />
                ) : null}
                <path d={line(s.values)} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              </g>
            ))}
            {n > 0
              ? ends.map(({ s, v }) => (
                  <g key={s.key}>
                    <circle cx={x(n - 1)} cy={y(v)} r={4} fill={s.color} stroke="var(--chart-surface)" strokeWidth={2} />
                    {endsFit ? (
                      <text className={styles.endLabel} x={x(n - 1) - 8} y={y(v) - 9} textAnchor="end">{format(v)}</text>
                    ) : null}
                  </g>
                ))
              : null}
            {hover !== null && n > 0 ? (
              <g>
                <line className={styles.cross} x1={Math.round(x(hover)) + 0.5} x2={Math.round(x(hover)) + 0.5} y1={TOP} y2={TOP + ih} />
                {series.map((s) => (
                  <circle key={s.key} cx={x(hover)} cy={y(s.values[hover] ?? 0)} r={4} fill={s.color} stroke="var(--chart-surface)" strokeWidth={2} />
                ))}
              </g>
            ) : null}
            <rect
              x={0}
              y={0}
              width={width}
              height={H}
              fill="transparent"
              onPointerMove={(e: PointerEvent<SVGRectElement>) => pick(e.clientX, e.currentTarget)}
              onPointerDown={(e: PointerEvent<SVGRectElement>) => pick(e.clientX, e.currentTarget)}
              onPointerLeave={() => setHover(null)}
            />
          </svg>
        ) : (
          <div style={{ height: H }} />
        )}
        {hover !== null && n > 0 && width > 0 ? (
          <div className={styles.tip} style={x(hover) > width - 170 ? { right: width - x(hover) + 12 } : { left: x(hover) + 12 }}>
            <div className={styles.tipDate}>{longDay(days[hover]!)}</div>
            {series.map((s) => (
              <div key={s.key} className={styles.tipRow}>
                <span className={styles.key} style={{ background: s.color }} />
                <span className={styles.tipValue}>{format(s.values[hover] ?? 0)}</span>
                <span className={styles.tipLabel}>{s.label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  )
}

/** Сплайн для плитки: весь период приглушённым цветом, последний день — точкой акцента. */
export function Sparkline({ values }: { values: number[] }) {
  const w = 112
  const h = 28
  const n = values.length
  if (n < 2) return null
  const max = Math.max(1, ...values)
  const px = (i: number) => 2 + (i * (w - 6)) / (n - 1)
  const py = (v: number) => h - 3 - (v / max) * (h - 6)
  const d = values.map((v, i) => `${i ? 'L' : 'M'}${px(i).toFixed(1)},${py(v).toFixed(1)}`).join('')
  return (
    <svg className={styles.spark} width={w} height={h} aria-hidden="true">
      <path d={d} fill="none" stroke="var(--text-muted)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" opacity={0.7} />
      <circle cx={px(n - 1)} cy={py(values[n - 1]!)} r={3} fill="var(--chart-1)" />
    </svg>
  )
}

/** Воронка: полосы от одной базы (первый шаг), значение и доля — у конца полосы. */
export function FunnelBars({ steps }: { steps: { label: string; value: number; note?: string }[] }) {
  const base = steps[0]?.value ?? 0
  return (
    <div className={styles.funnel}>
      {steps.map((s) => {
        const share = base > 0 ? Math.max(0, Math.min(100, (s.value / base) * 100)) : 0
        return (
          <div key={s.label} className={styles.step}>
            <span className={styles.stepLabel}>{s.label}</span>
            <span className={styles.stepTrack}>
              <span className={styles.bar} style={{ width: `calc(${share}% * 0.8)` }} />
              <span className={styles.stepValue}>
                <b>{fmtInt(s.value)}</b>
                {/* доля от первого шага — только если шаг его не превышает: иначе шаги считают разное */}
                {base > 0 && s.value <= base ? ` · ${pct(s.value, base)}%` : ''}
                {s.note ? ` · ${s.note}` : ''}
              </span>
            </span>
          </div>
        )
      })}
    </div>
  )
}

/*
 * Когорты: строка — неделя регистрации, столбец — неделя после неё. Цвет — один оттенок,
 * насыщеннее там, где доля выше; цифра в ячейке всегда видна, так что цвет только помогает.
 */
export function CohortTable({ cohorts }: { cohorts: { week: string; size: number; active: number[] }[] }) {
  const cols = Math.max(0, ...cohorts.map((c) => c.active.length))
  return (
    <div className={styles.cohorts}>
      <table className={styles.cohortTable}>
        <thead>
          <tr>
            <th>Неделя регистрации</th>
            <th>Людей</th>
            {Array.from({ length: cols }, (_, i) => (
              <th key={i}>{i === 0 ? 'та же' : `+${i}`}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => (
            <tr key={c.week}>
              <td>с {shortDay(c.week)}</td>
              <td>{fmtInt(c.size)}</td>
              {Array.from({ length: cols }, (_, i) => {
                if (i >= c.active.length) return <td key={i} />
                const p = pct(c.active[i] ?? 0, c.size)
                const strong = p > 55
                return (
                  <td
                    key={i}
                    className={`${styles.cell} ${strong ? styles.cellStrong : ''}`}
                    style={{ background: c.size > 0 ? `color-mix(in srgb, var(--chart-1) ${Math.max(6, p)}%, transparent)` : undefined }}
                    data-tooltip={`Неделя с ${shortDay(c.week)}: ${fmtInt(c.active[i] ?? 0)} из ${fmtInt(c.size)} активны ${i === 0 ? 'в неделю регистрации' : `через ${i} нед.`}`}
                  >
                    {c.size > 0 ? `${p}%` : '—'}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
